import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, FileText, TrendingUp, Users, CheckCircle2, XCircle, Clock,
  ChevronRight, Ban, Play, MessageSquare, Download, Search, RefreshCw, Wallet,
} from "lucide-react";

function usePagination(items, perPage = 10) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(items.length / perPage);
  const paginated = items.slice((page - 1) * perPage, page * perPage);
  return { page, setPage, totalPages, paginated };
}

function Pagination({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
      <button
        onClick={() => setPage(Math.max(1, page - 1))}
        disabled={page === 1}
        className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white disabled:opacity-30"
      >Prev</button>
      <span className="text-[10px] text-zinc-600 font-mono">{page}/{totalPages}</span>
      <button
        onClick={() => setPage(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white disabled:opacity-30"
      >Next</button>
    </div>
  );
}

export default function AdminReferrals() {
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [w9Queue, setW9Queue] = useState([]);
  const [w9Total, setW9Total] = useState(0);
  const [w9Status, setW9Status] = useState("pending");
  const [referrerDetail, setReferrerDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAnalytics();
    loadW9Queue("pending");
  }, []);

  const loadAnalytics = async () => {
    try {
      const r = await api.get("/admin/referrals/analytics");
      setAnalytics(r.data);
    } catch (e) {
      toast.error("Failed to load referral analytics");
    }
  };

  const loadW9Queue = async (status) => {
    setW9Status(status);
    try {
      const r = await api.get("/admin/w9-queue", { params: { status, limit: 100 } });
      setW9Queue(r.data.submissions || []);
      setW9Total(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load W-9 queue");
    }
  };

  const handleW9Review = async (submissionId, action, reason = null) => {
    setActionLoading(submissionId);
    try {
      await api.post("/admin/w9-review", {
        submission_id: submissionId,
        action,
        rejection_reason: reason,
      });
      toast.success(`W-9 ${action}d successfully`);
      loadW9Queue(w9Status);
      loadAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} W-9`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCommissionControl = async (referrerUserId, referredUserId, action, reason = "") => {
    setActionLoading(`${referrerUserId}-${referredUserId}`);
    try {
      await api.post("/admin/referrals/commission-control", {
        referrer_user_id: referrerUserId,
        referred_user_id: referredUserId,
        action,
        reason: reason || `Admin ${action} via dashboard`,
      });
      toast.success(`Commission ${action === "stop" ? "stopped" : "resumed"}`);
      if (referrerDetail) loadReferrerDetail(referrerUserId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update commission");
    } finally {
      setActionLoading(null);
    }
  };

  const loadReferrerDetail = async (userId) => {
    try {
      const r = await api.get(`/admin/referrals/referrer/${userId}`);
      setReferrerDetail(r.data);
      setActiveTab("detail");
    } catch (e) {
      toast.error("Failed to load referrer detail");
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([loadAnalytics(), loadW9Queue(w9Status)]);
    setRefreshing(false);
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: TrendingUp },
    { key: "w9", label: `W-9 Queue${analytics?.w9?.pending ? ` (${analytics.w9.pending})` : ""}`, icon: FileText },
    { key: "referrers", label: "Referrers", icon: Users },
  ];
  if (referrerDetail) tabs.push({ key: "detail", label: referrerDetail.user?.shop_name || "Detail", icon: ChevronRight });

  return (
    <div className="space-y-6">
      {/* Sub-nav */}
      <div className="flex items-center gap-1 border-b border-white/10 pb-0.5 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.key
                ? "border-white text-white"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <t.icon size={12} />
            {t.label}
          </button>
        ))}
        <button onClick={refresh} className="ml-auto p-2 text-zinc-500 hover:text-white">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {activeTab === "overview" && <OverviewPanel analytics={analytics} onViewReferrer={loadReferrerDetail} />}
      {activeTab === "w9" && (
        <W9Panel
          queue={w9Queue}
          total={w9Total}
          status={w9Status}
          onStatusChange={loadW9Queue}
          onReview={handleW9Review}
          actionLoading={actionLoading}
        />
      )}
      {activeTab === "referrers" && <ReferrersPanel analytics={analytics} onViewReferrer={loadReferrerDetail} />}
      {activeTab === "detail" && referrerDetail && (
        <ReferrerDetailPanel
          data={referrerDetail}
          onCommissionControl={handleCommissionControl}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}

/* OVERVIEW PANEL */

function OverviewPanel({ analytics, onViewReferrer }) {
  if (!analytics) return <div className="p-12 text-center text-zinc-500 italic">Loading analytics...</div>;
  const { links, earnings, w9 } = analytics;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Active Links" value={links.active} sub={`${links.conversion_rate}% conv.`} color="emerald" />
        <KPICard label="Total Issued" value={`$${earnings.total_issued.toFixed(2)}`} sub={`$${earnings.pending_payout.toFixed(2)} pending`} color="white" />
        <KPICard label="New (7d)" value={links.new_7d} sub={`${links.new_30d} in 30d`} color="blue" />
        <KPICard label="W-9 Pending" value={w9.pending} sub={`${w9.approved} approved`} color={w9.pending > 0 ? "amber" : "zinc"} />
      </div>

      {/* Monthly Trend */}
      {earnings.monthly_trend.length > 0 && (
        <div className="border border-white/10 bg-[#121214] p-6">
          <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-4">Monthly Commission Trend</h3>
          <div className="space-y-2">
            {earnings.monthly_trend.map((m) => (
              <div key={m.month} className="flex items-center gap-4">
                <span className="text-xs font-mono text-zinc-500 w-20">{m.month}</span>
                <div className="flex-1 h-4 bg-white/5 relative overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/30 border-r border-emerald-500"
                    style={{ width: `${Math.min(100, (m.amount / Math.max(...earnings.monthly_trend.map(x => x.amount), 1)) * 100)}%` }}
                  />
                </div>
                <span className="text-xs font-bold w-20 text-right">${m.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-[#121214] p-6">
          <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-4">Link Breakdown</h3>
          <div className="space-y-3">
            <StatRow label="Active" value={links.active} color="emerald" />
            <StatRow label="Pending" value={links.pending} color="amber" />
            <StatRow label="Stopped" value={links.stopped} color="red" />
            <StatRow label="Total" value={links.total} color="white" bold />
          </div>
        </div>
        <div className="border border-white/10 bg-[#121214] p-6">
          <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-4">Payout Summary</h3>
          <div className="space-y-3">
            <StatRow label="Total Issued" value={`$${earnings.total_issued.toFixed(2)}`} color="white" />
            <StatRow label="Paid Out" value={`$${earnings.paid_out.toFixed(2)}`} color="emerald" />
            <StatRow label="Pending" value={`$${earnings.pending_payout.toFixed(2)}`} color="amber" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* W-9 PANEL */

function W9Panel({ queue, total, status, onStatusChange, onReview, actionLoading }) {
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const { page, setPage, totalPages, paginated } = usePagination(queue, 10);

  const statusTabs = ["pending", "approved", "rejected", "all"];

  return (
    <div className="space-y-4">
      {/* Status filter */}
      <div className="flex gap-1">
        {statusTabs.map((s) => (
          <button
            key={s}
            onClick={() => onStatusChange(s)}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
              status === s
                ? "border-white text-white"
                : "border-white/10 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-500 self-center font-mono">{total} total</span>
      </div>

      {/* Queue table */}
      <div className="border border-white/10 bg-[#121214] overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
              <th className="p-4">Name</th>
              <th className="p-4">Shop</th>
              <th className="p-4">Last 4</th>
              <th className="p-4">State</th>
              <th className="p-4">Submitted</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paginated.map((sub) => (
              <tr key={sub.submission_id} className="text-xs">
                <td className="p-4 font-bold">{sub.legal_name}</td>
                <td className="p-4 text-zinc-400">{sub.shop_name}</td>
                <td className="p-4 font-mono">••{sub.tax_id_last4}</td>
                <td className="p-4">{sub.state}</td>
                <td className="p-4 text-zinc-500 font-mono">
                  {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-4">
                  <StatusBadge status={sub.status} />
                </td>
                <td className="p-4 text-right">
                  {sub.status === "pending" && (
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => onReview(sub.submission_id, "approve")}
                        disabled={actionLoading === sub.submission_id}
                        className="p-1.5 border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-30"
                        title="Approve"
                      >
                        <CheckCircle2 size={12} />
                      </button>
                      <button
                        onClick={() => setRejectId(sub.submission_id)}
                        disabled={actionLoading === sub.submission_id}
                        className="p-1.5 border border-red-500/30 text-red-500 hover:bg-red-500/10 disabled:opacity-30"
                        title="Reject"
                      >
                        <XCircle size={12} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-600 italic">No submissions</td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {/* Rejection modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRejectId(null)}>
          <div className="bg-[#121214] border border-white/10 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Reject W-9</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)..."
              className="w-full bg-[#09090B] border border-white/10 px-4 py-3 text-sm h-24 resize-none focus:border-white/30 focus:outline-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setRejectId(null); setRejectReason(""); }}
                className="flex-1 py-2.5 border border-white/10 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white"
              >Cancel</button>
              <button
                onClick={() => {
                  if (!rejectReason.trim()) return toast.error("Rejection reason is required");
                  onReview(rejectId, "reject", rejectReason);
                  setRejectId(null);
                  setRejectReason("");
                }}
                className="flex-1 py-2.5 bg-red-500/20 border border-red-500/30 text-xs font-bold uppercase tracking-widest text-red-500 hover:bg-red-500/30"
              >Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* REFERRERS PANEL */

function ReferrersPanel({ analytics, onViewReferrer }) {
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { page, setPage, totalPages, paginated } = usePagination(referrers, 15);

  useEffect(() => {
    loadReferrers();
  }, []);

  const loadReferrers = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/referrals");
      setReferrers(r.data.top_referrers || []);
    } catch (e) {
      toast.error("Failed to load referrers");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-zinc-500 italic">Loading referrers...</div>;

  return (
    <div className="border border-white/10 bg-[#121214] overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.02] uppercase tracking-widest text-[10px] font-bold text-zinc-500">
            <th className="p-4">Shop</th>
            <th className="p-4">Email</th>
            <th className="p-4 text-center">Active</th>
            <th className="p-4 text-center">Total</th>
            <th className="p-4 text-right">Earned</th>
            <th className="p-4">Payout</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {paginated.map((r) => (
            <tr key={r.user_id} className="text-xs hover:bg-white/[0.02] cursor-pointer" onClick={() => onViewReferrer(r.user_id)}>
              <td className="p-4 font-bold">{r.shop_name}</td>
              <td className="p-4 text-zinc-400">{r.email}</td>
              <td className="p-4 text-center text-emerald-500 font-bold">{r.active_referrals}</td>
              <td className="p-4 text-center">{r.total_referrals}</td>
              <td className="p-4 text-right font-bold font-mono">${r.total_earned.toFixed(2)}</td>
              <td className="p-4">
                {r.payout_method ? (
                  <div className="flex items-center gap-1.5">
                    <Wallet size={11} className="text-emerald-500" />
                    <span className="text-[10px] font-bold uppercase text-emerald-500">{r.payout_method.method}</span>
                    <span className="text-[10px] text-zinc-500 truncate max-w-[80px]">{r.payout_method.handle}</span>
                  </div>
                ) : (
                  <span className="text-[10px] text-zinc-600 italic">None</span>
                )}
              </td>
              <td className="p-4 text-right">
                <button className="p-1 text-zinc-500 hover:text-white"><ChevronRight size={14} /></button>
              </td>
            </tr>
          ))}
          {paginated.length === 0 && (
            <tr><td colSpan={7} className="p-8 text-center text-zinc-600 italic">No referrers yet</td></tr>
          )}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

/* REFERRER DETAIL PANEL */

function ReferrerDetailPanel({ data, onCommissionControl, actionLoading }) {
  const { user, links, earnings, total_earned, active_referrals, w9 } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border border-white/10 bg-[#121214] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest">{user.shop_name || "Unknown Shop"}</h2>
            <p className="text-xs text-zinc-500 mt-1">{user.email} | Code: <span className="font-mono text-zinc-300">{user.referral_code}</span></p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black">${total_earned.toFixed(2)}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Total Earned</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 pt-4 border-t border-white/5">
          <div className="text-center">
            <div className="text-lg font-bold text-emerald-500">{active_referrals}</div>
            <div className="text-[10px] text-zinc-500 uppercase">Active</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold">{links.length}</div>
            <div className="text-[10px] text-zinc-500 uppercase">Total Links</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold">{w9 ? <StatusBadge status={w9.status} /> : <span className="text-red-500">None</span>}</div>
            <div className="text-[10px] text-zinc-500 uppercase">W-9</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold">
              {data.payout_method ? (
                <span className="text-emerald-500 text-sm font-bold uppercase">{data.payout_method.method}</span>
              ) : (
                <span className="text-red-500">None</span>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 uppercase">Payout</div>
          </div>
        </div>
      </div>

      {/* Referral Links */}
      <div className="border border-white/10 bg-[#121214] overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Referral Links</h3>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/5 text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
              <th className="px-4 py-2">Referred Shop</th>
              <th className="px-4 py-2">Tier</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {links.map((link, idx) => {
              const isLoading = actionLoading === `${user.user_id}-${link.referred_user_id}`;
              return (
                <tr key={idx} className="text-xs">
                  <td className="px-4 py-3">
                    <div className="font-bold">{link.referee_shop_name}</div>
                    <div className="text-[10px] text-zinc-600">{link.referee_email}</div>
                  </td>
                  <td className="px-4 py-3 uppercase text-[10px] font-bold">{link.referee_tier}</td>
                  <td className="px-4 py-3"><StatusBadge status={link.status} /></td>
                  <td className="px-4 py-3 text-zinc-500 font-mono">{link.referred_at ? new Date(link.referred_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {link.commission_permanently_stopped ? (
                      <button
                        onClick={() => onCommissionControl(user.user_id, link.referred_user_id, "resume")}
                        disabled={isLoading}
                        className="p-1.5 border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-30"
                        title="Resume commission"
                      >
                        <Play size={11} />
                      </button>
                    ) : link.status === "active" ? (
                      <button
                        onClick={() => onCommissionControl(user.user_id, link.referred_user_id, "stop")}
                        disabled={isLoading}
                        className="p-1.5 border border-red-500/30 text-red-500 hover:bg-red-500/10 disabled:opacity-30"
                        title="Stop commission"
                      >
                        <Ban size={11} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Earnings History */}
      {earnings.length > 0 && (
        <div className="border border-white/10 bg-[#121214] overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10">
            <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Earnings History (Last 100)</h3>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[10px] font-bold text-zinc-600 uppercase tracking-widest sticky top-0 bg-[#121214]">
                  <th className="px-4 py-2">Month</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {earnings.map((e, idx) => (
                  <tr key={idx} className="text-xs">
                    <td className="px-4 py-2 font-mono">{e.month}</td>
                    <td className="px-4 py-2 font-bold">${parseFloat(e.amount).toFixed(2)}</td>
                    <td className="px-4 py-2 uppercase text-[10px]">{e.tier}</td>
                    <td className="px-4 py-2"><StatusBadge status={e.payout_status || e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* SHARED COMPONENTS */

function KPICard({ label, value, sub, color }) {
  const colorMap = {
    emerald: "border-emerald-500/20 text-emerald-500",
    amber: "border-amber-500/20 text-amber-500",
    red: "border-red-500/20 text-red-500",
    blue: "border-blue-500/20 text-blue-500",
    white: "border-white/20 text-white",
    zinc: "border-white/10 text-zinc-400",
  };
  const cls = colorMap[color] || colorMap.zinc;
  return (
    <div className={`border ${cls.split(" ")[0]} bg-[#121214] p-5`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">{label}</div>
      <div className={`text-2xl font-black ${cls.split(" ")[1]}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function StatRow({ label, value, color, bold }) {
  const colorMap = { emerald: "text-emerald-500", amber: "text-amber-500", red: "text-red-500", white: "text-white", zinc: "text-zinc-400" };
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className={`text-xs ${bold ? "font-black" : "font-bold"} ${colorMap[color] || "text-white"}`}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "border-emerald-500/30 text-emerald-500",
    approved: "border-emerald-500/30 text-emerald-500",
    pending: "border-amber-500/30 text-amber-500",
    pending_payout: "border-amber-500/30 text-amber-500",
    rejected: "border-red-500/30 text-red-500",
    commission_ended: "border-red-500/30 text-red-500",
    paid: "border-emerald-500/30 text-emerald-500",
    failed: "border-red-500/30 text-red-500",
  };
  const label = status === "pending_payout" ? "pending" : status === "commission_ended" ? "ended" : status;
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${map[status] || "border-zinc-500/30 text-zinc-500"}`}>
      {label || "unknown"}
    </span>
  );
}
